import React, { useEffect, useState } from "react";
import { Wrench, MapPin, Phone, Mail, Globe, Search, Save, FileText, X, Star, MessageSquarePlus, MessageSquare, Sparkles, Upload, Loader2, Check } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export default function Artisans() {
  const { user } = useAuth();
  const isArtisan = user?.role === "artisan";
  const [domains, setDomains] = useState([]);

  useEffect(() => { api.get("/artisans/domains").then((r) => setDomains(r.data)).catch(() => {}); }, []);
  return isArtisan ? <ArtisanProfile domains={domains} /> : <Directory domains={domains} />;
}

function Directory({ domains }) {
  const navigate = useNavigate();
  const [city, setCity] = useState("");
  const [domain, setDomain] = useState("Tous");
  const [list, setList] = useState([]);
  const [desc, setDesc] = useState("");
  const [properties, setProperties] = useState([]);
  const [reqProp, setReqProp] = useState("");
  const [reqPhoto, setReqPhoto] = useState("");
  const [reqDiag, setReqDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [reviewArtisan, setReviewArtisan] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  // Demande de travaux (propriétaire) : artisan verrouillé (depuis la fiche) ou choisi/proposé par l'IA (depuis le haut de page)
  const [reqOpen, setReqOpen] = useState(false);
  const [reqArtisanId, setReqArtisanId] = useState("");
  const [reqLocked, setReqLocked] = useState(false);

  const search = () => {
    const params = {};
    if (city) params.city = city;
    if (domain && domain !== "Tous") params.domain = domain;
    api.get("/artisans/directory", { params }).then((r) => setList(r.data)).catch(() => {});
  };
  useEffect(() => { search(); api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {}); /* eslint-disable-next-line */ }, []);

  const resetReq = () => { setReqOpen(false); setReqArtisanId(""); setReqLocked(false); setDesc(""); setReqProp(""); setReqPhoto(""); setReqDiag(null); setSrcMission(""); setReqPhotoUrl(""); };
  const openRequest = (a) => { resetReq(); setReqOpen(true); setReqArtisanId(a.id); setReqLocked(true); };
  const openRequestTop = () => { resetReq(); setReqOpen(true); setReqLocked(false); };
  const location = useLocation();
  const [srcMission, setSrcMission] = useState("");
  const [reqPhotoUrl, setReqPhotoUrl] = useState("");
  useEffect(() => {
    const pf = location.state?.workPrefill;
    if (!pf) return;
    openRequestTop(); setDesc(pf.description || ""); setReqProp(pf.property || ""); setSrcMission(pf.mission_id || "");
    if (pf.photo_url) {
      setReqPhotoUrl(pf.photo_url);
      api.get(pf.photo_url.replace(/^\/api/, ""), { responseType: "blob" }).then((r) => { const fr = new FileReader(); fr.onload = () => setReqPhoto(String(fr.result).split(",")[1]); fr.readAsDataURL(r.data); }).catch(() => {});
    }
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line
  }, []);

  const runReqDiagnostic = async () => {
    if (!desc.trim() && !reqPhoto) { toast.error("Décrivez les travaux ou ajoutez une photo."); return; }
    setDiagBusy(true);
    try {
      const r = await api.post("/ai/diagnose-work", { description: desc.trim(), image_base64: reqPhoto });
      setReqDiag(r.data);
      // L'IA propose le meilleur artisan référencé (uniquement si l'artisan n'est pas déjà verrouillé)
      if (!reqLocked && r.data.suggested_artisans?.length) {
        setReqArtisanId(r.data.suggested_artisans[0].id);
        toast.success(`Diagnostic IA effectué · artisan recommandé : ${r.data.suggested_artisans[0].company || r.data.suggested_artisans[0].name}`);
      } else {
        toast.success("Diagnostic IA effectué");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Diagnostic indisponible"); }
    finally { setDiagBusy(false); }
  };

  const contact = async (a) => {
    try {
      const res = await api.post("/conversations/start", { name: a.company || a.name, subject: `Bonjour, je vous contacte au sujet de vos prestations (${a.domain}).` });
      toast.success(res.data.existing ? "Conversation existante ouverte" : `Conversation démarrée avec ${a.company}`);
      navigate("/app/messagerie");
    } catch (e) { toast.error("Impossible de démarrer la conversation"); }
  };

  const submitRequest = async () => {
    if (!reqArtisanId) { toast.error("Choisissez un artisan (ou lancez le diagnostic IA)."); return; }
    if (!desc.trim()) { toast.error("Décrivez vos travaux"); return; }
    try {
      await api.post("/works/requests", { artisan_id: reqArtisanId, description: desc, property: reqProp, diagnostic: reqDiag || null, photo_base64: reqPhoto || "", source_mission_id: srcMission });
      const a = list.find((x) => x.id === reqArtisanId);
      toast.success(`Demande envoyée${a ? ` à ${a.company || a.name}` : ""} ! Suivez-la dans Travaux.`);
      resetReq();
      navigate("/app/travaux");
    } catch (e) { toast.error("Erreur lors de l'envoi"); }
  };

  const submitReview = async () => {
    try {
      await api.post(`/artisans/${reviewArtisan.id}/reviews`, { rating, comment });
      toast.success("Merci pour votre avis !");
      setReviewArtisan(null); setComment(""); setRating(5);
      search();
    } catch (e) { toast.error("Erreur lors de l'envoi de l'avis"); }
  };

  return (
    <div className="space-y-6">
      {/* Demande de travaux assistée par IA (propriétaire) */}
      <div className="bg-brand-greenDeep rounded-2xl p-5 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="owner-work-request-banner">
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-xl bg-brand-lime/20 flex items-center justify-center shrink-0"><Wrench className="w-5 h-5 text-brand-lime" /></span>
          <div>
            <h3 className="font-semibold">Créer une demande de travaux</h3>
            <p className="text-xs text-white/60 mt-0.5 max-w-lg">Décrivez le problème : le diagnostic IA identifie la spécialité et vous propose le meilleur artisan référencé. Ou cliquez directement sur la fiche d'un artisan.</p>
          </div>
        </div>
        <button onClick={openRequestTop} className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors" data-testid="owner-new-request-btn">
          <Sparkles className="w-4 h-4" /> Nouvelle demande
        </button>
      </div>

      <div className="bg-white rounded-2xl p-5 border border-brand-soft flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-softer flex-1">
          <MapPin className="w-4 h-4 text-brand-green/40" />
          <input value={city} onChange={(e) => setCity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Ville (ex: Paris)" className="bg-transparent outline-none text-sm w-full" />
        </div>
        <select value={domain} onChange={(e) => setDomain(e.target.value)} className="px-4 py-2.5 rounded-full bg-brand-softer text-sm text-brand-green outline-none">
          <option>Tous</option>
          {domains.map((d) => <option key={d}>{d}</option>)}
        </select>
        <button onClick={search} className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors">
          <Search className="w-4 h-4" /> Rechercher
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {list.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl p-6 border border-brand-soft hover:shadow-lg transition-all">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center"><Wrench className="w-5 h-5 text-brand-greenDeep" /></span>
              <div>
                <h3 className="font-semibold text-brand-greenDeep">{a.company}</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-brand-lime/30 text-brand-greenDeep font-medium">{a.domain}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 mb-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`w-4 h-4 ${i <= Math.round(a.avg_rating || 0) ? "fill-brand-limeDark text-brand-limeDark" : "text-brand-soft"}`} />
              ))}
              <span className="text-xs text-brand-green/50 ml-1">{a.avg_rating ? `${a.avg_rating}/5` : "Aucun avis"} ({a.review_count || 0})</span>
            </div>
            <p className="text-sm text-brand-green/60">{a.description}</p>
            {a.recent_reviews && a.recent_reviews.length > 0 && (
              <div className="mt-3 space-y-2">
                {a.recent_reviews.map((rv) => (
                  <div key={rv.id} className="rounded-xl bg-brand-softer p-2.5">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`w-3 h-3 ${i <= rv.rating ? "fill-brand-limeDark text-brand-limeDark" : "text-brand-soft"}`} />
                      ))}
                      <span className="text-[11px] text-brand-green/50 ml-1">{rv.client_name}</span>
                    </div>
                    {rv.comment && <p className="text-xs text-brand-green/60 mt-1 italic">« {rv.comment} »</p>}
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 space-y-2 text-sm text-brand-green/70">
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-green/40" /> {a.city}</p>
              {a.address && <p className="flex items-start gap-2 text-xs text-brand-green/55"><MapPin className="w-3.5 h-3.5 text-brand-green/30 mt-0.5" /> {a.address}</p>}
              {a.intervention_zones && <p className="text-xs text-brand-green/55"><span className="font-medium">Zones :</span> {a.intervention_zones}</p>}
              {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-2 hover:text-brand-greenDeep"><Phone className="w-4 h-4 text-brand-green/40" /> {a.phone}</a>}
              {a.email && <a href={`mailto:${a.email}`} className="flex items-center gap-2 hover:text-brand-greenDeep"><Mail className="w-4 h-4 text-brand-green/40" /> {a.email}</a>}
              {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-brand-greenDeep"><Globe className="w-4 h-4 text-brand-green/40" /> Site web</a>}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => contact(a)} className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-emerald text-white font-semibold text-sm transition-colors" data-testid={`contact-artisan-${a.id}`}><MessageSquare className="w-4 h-4" /> Contacter</button>
              <button onClick={() => openRequest(a)} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-brand-soft text-brand-green font-medium text-sm hover:bg-brand-softer transition-colors" title="Demander un devis"><FileText className="w-4 h-4" /></button>
              <button onClick={() => setReviewArtisan(a)} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-brand-soft text-brand-green font-medium text-sm hover:bg-brand-softer transition-colors" title="Laisser un avis"><MessageSquarePlus className="w-4 h-4" /></button>
              {a.phone && <a href={`tel:${a.phone}`} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-brand-soft text-brand-green font-medium text-sm hover:bg-brand-softer transition-colors"><Phone className="w-4 h-4" /></a>}
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="col-span-full text-center py-16 text-brand-green/50">Aucun artisan trouvé pour ces critères.</p>}
      </div>

      {reqOpen && (() => {
        const chosen = list.find((x) => x.id === reqArtisanId);
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={resetReq}>
          <div className="bg-white rounded-3xl p-8 w-full max-w-md max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="work-request-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">{reqLocked ? "Demander un devis" : "Nouvelle demande de travaux"}</h3>
              <button onClick={resetReq} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
            </div>
            {reqLocked && chosen
              ? <p className="text-sm text-brand-green/50 mb-4">{chosen.company} · {chosen.domain} · {chosen.city}</p>
              : <p className="text-sm text-brand-green/50 mb-4">Décrivez le problème puis lancez le diagnostic IA : il identifie la spécialité et vous propose le meilleur artisan référencé.</p>}
            {properties.length > 0 && (
              <select value={reqProp} onChange={(e) => setReqProp(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-lime mb-3 text-sm" data-testid="req-property">
                <option value="">— Bien concerné (optionnel) —</option>
                {properties.map((p) => <option key={p.id} value={p.name}>{p.name || p.address}</option>)}
              </select>
            )}
            {srcMission && <div className="mb-3 flex items-center gap-3 rounded-xl bg-red-50 border border-red-100 p-2.5" data-testid="req-from-mission">{reqPhotoUrl && <img src={`${process.env.REACT_APP_BACKEND_URL}${reqPhotoUrl}`} alt="Signalement" className="w-14 h-14 rounded-lg object-cover" />}<p className="text-xs text-red-700">Pré-rempli depuis le signalement de l'intervenant. Lancez le diagnostic IA pour trouver l'artisan.</p></div>}
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} placeholder="Décrivez les travaux à réaliser..." className="w-full px-4 py-3 rounded-2xl border border-brand-soft outline-none focus:border-brand-lime transition-all" data-testid="req-desc" />
            <div className="mt-3 rounded-xl border border-brand-soft bg-brand-softer/50 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-brand-soft cursor-pointer text-xs text-brand-green/60 hover:border-brand-lime bg-white">
                  <Upload className="w-3.5 h-3.5" /> {reqPhoto ? "Photo ajoutée ✓" : "Ajouter une photo"}
                  <input type="file" accept="image/*" className="hidden" data-testid="req-photo" onChange={(e) => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = () => setReqPhoto(String(r.result).split(",")[1]); r.readAsDataURL(f); } }} />
                </label>
                <button type="button" onClick={runReqDiagnostic} disabled={diagBusy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-xs font-semibold disabled:opacity-60" data-testid="req-diagnose-btn">
                  {diagBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Diagnostic IA
                </button>
              </div>
              {reqDiag && (
                <div className="mt-3 text-sm" data-testid="req-diag-result">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-brand-lime/30 text-brand-greenDeep">{reqDiag.category}</span>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${reqDiag.urgency === "Critique" ? "bg-red-100 text-red-700" : reqDiag.urgency === "Élevée" ? "bg-orange-100 text-orange-700" : reqDiag.urgency === "Moyenne" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>Urgence : {reqDiag.urgency}</span>
                  </div>
                  {reqDiag.summary && <p className="text-brand-green/70 mt-2 text-xs">{reqDiag.summary}</p>}
                  {reqDiag.suggested_artisans?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-brand-greenDeep">Artisans recommandés ({reqDiag.category})</p>
                      {reqDiag.suggested_artisans.map((a, idx) => (
                        <button type="button" key={a.id} onClick={() => setReqArtisanId(a.id)} className={`w-full flex items-center gap-2 mt-1 px-2 py-1.5 rounded-lg text-left transition-colors ${reqArtisanId === a.id ? "bg-brand-lime/20 ring-1 ring-brand-limeDark" : "hover:bg-brand-softer"}`} data-testid={`req-suggested-${a.id}`}>
                          {idx === 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-brand-limeDark text-white shrink-0">TOP</span>}
                          <span className="flex-1 text-xs text-brand-green/70 truncate">{a.company || a.name} · {a.city}</span>
                          {reqArtisanId === a.id && <Check className="w-3.5 h-3.5 text-brand-limeDark shrink-0" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            {!reqLocked && (
              <div className="mt-3">
                <label className="text-xs font-semibold text-brand-green/60">Artisan sélectionné</label>
                <select value={reqArtisanId} onChange={(e) => setReqArtisanId(e.target.value)} className="w-full mt-1 px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-lime text-sm" data-testid="req-artisan-select">
                  <option value="">— Choisir un artisan —</option>
                  {list.map((a) => <option key={a.id} value={a.id}>{`${a.company || a.name} · ${a.domain} · ${a.city}`}</option>)}
                </select>
              </div>
            )}
            <div className="flex gap-3 mt-4">
              <button onClick={resetReq} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-green font-semibold">Annuler</button>
              <button onClick={submitRequest} disabled={!reqArtisanId} className="flex-1 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors disabled:opacity-50" data-testid="req-submit-btn">Soumettre la demande</button>
            </div>
          </div>
        </div>
        );
      })()}

      {reviewArtisan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setReviewArtisan(null)}>
          <div className="bg-white rounded-3xl p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">Laisser un avis</h3>
              <button onClick={() => setReviewArtisan(null)} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-brand-green/50 mb-5">{reviewArtisan.company} · {reviewArtisan.domain}</p>
            <div className="flex items-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} onClick={() => setRating(i)} type="button">
                  <Star className={`w-8 h-8 transition-colors ${i <= rating ? "fill-brand-limeDark text-brand-limeDark" : "text-brand-soft"}`} />
                </button>
              ))}
            </div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Votre commentaire (optionnel)..." className="w-full px-4 py-3 rounded-2xl border border-brand-soft outline-none focus:border-brand-lime focus:ring-2 focus:ring-brand-lime/30 transition-all" />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setReviewArtisan(null)} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-green font-semibold">Annuler</button>
              <button onClick={submitReview} className="flex-1 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors">Publier l'avis</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ArtisanProfile({ domains }) {
  const [form, setForm] = useState({ company: "", domain: "Plomberie", city: "", phone: "", email: "", website: "", description: "", address: "", intervention_zones: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/artisans/me").then((r) => { if (r.data && r.data.company) setForm({ ...form, ...r.data }); }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.company || !form.city) { toast.error("Société et ville requises"); return; }
    setSaving(true);
    try {
      await api.put("/artisans/me", {
        company: form.company, domain: form.domain, city: form.city,
        phone: form.phone, email: form.email, website: form.website, description: form.description,
        address: form.address, intervention_zones: form.intervention_zones,
      });
      toast.success("Profil enregistré — vous êtes visible dans l'annuaire");
    } catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-2xl p-8 border border-brand-soft">
        <h3 className="font-display text-2xl font-semibold text-brand-greenDeep mb-1">Mon profil artisan</h3>
        <p className="text-sm text-brand-green/60 mb-6">Ces informations seront visibles par les propriétaires dans l'annuaire.</p>
        <form onSubmit={save} className="space-y-4">
          <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Nom de la société" className="input" />
          <div className="grid grid-cols-2 gap-4">
            <select value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} className="input">
              {domains.map((d) => <option key={d}>{d}</option>)}
            </select>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" className="input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone" className="input" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="input" />
          </div>
          <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Site web (https://...)" className="input" />
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresse (numéro, rue, code postal, ville)" className="input" data-testid="artisan-address" />
          <textarea value={form.intervention_zones} onChange={(e) => setForm({ ...form, intervention_zones: e.target.value })} placeholder="Zones d'intervention (ex. Paris, Boulogne, 92, 93...)" rows={2} className="input" data-testid="artisan-zones" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Décrivez votre activité" rows={3} className="input" />
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer mon profil"}
          </button>
        </form>
      </div>
      <style>{`.input{width:100%;padding:0.7rem 1rem;border-radius:1rem;border:1px solid #efe7d6;outline:none;transition:all .2s}.input:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.25)}`}</style>
    </div>
  );
}
